/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */

/*
 * Il magazzino dell'azienda cliente, tenuto dall'azienda stessa.
 * Niente foto e niente allegati: solo righe di testo e numeri.
 *
 * La giacenza non si modifica scrivendoci sopra: si registra un carico, uno
 * scarico o una rettifica da conteggio. Costa un tocco in piu' ma e' l'unica
 * ragione per cui, il giorno in cui il numero non torna, si puo' capire perche'.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Boxes, Plus, Search, X, Edit2, AlertTriangle, History,
  ArrowDownCircle, ArrowUpCircle, ClipboardCheck, Archive, RotateCcw, MapPin, Trash2,
} from 'lucide-react';
import ClienteLayout from '../components/ClienteLayout';
import { inventarioAPI } from '../api/inventario';
import { useProfilo } from '../hooks/useProfilo';
import { useViewport } from '../hooks/useViewport';

const ACCENT = '#3B82F6';

// Le quantita' sono numeric(12,3): "3.000" letto dal database va mostrato "3",
// e "0.500" va mostrato "0,5", con la virgola come si scrive in italiano.
function qta(v) {
  const n = parseFloat(v ?? 0);
  if (!Number.isFinite(n)) return '0';
  return n.toFixed(3).replace(/\.?0+$/, '').replace('.', ',') || '0';
}

/*
 * Lo stesso controllo che fa il backend, ripetuto qui per dirlo MENTRE si scrive
 * invece che dopo aver premuto Salva. Il backend resta l'autorita': questo e'
 * cortesia, non sicurezza.
 *
 * Non usa parseFloat apposta: parseFloat("3abc") vale 3, e salverebbe in
 * silenzio un numero che nessuno ha scritto.
 */
function erroreNumero(v, { obbligatorio = false } = {}) {
  const t = String(v ?? '').trim();
  if (t === '') return obbligatorio ? 'Scrivi una quantita' : null;
  if (!/^\d+([.,]\d+)?$/.test(t)) return 'Solo numeri: per esempio 12 oppure 12,5';
  return null;
}

function dataOra(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const TIPO_LABEL = {
  creazione: 'Giacenza iniziale',
  carico: 'Carico',
  scarico: 'Scarico',
  rettifica: 'Rettifica da conteggio',
};

export default function Inventario() {
  const { data: profilo, isLoading: profiloLoading } = useProfilo();
  const { isCompact } = useViewport();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  // Un solo filtro alla volta fra i tre riquadri: 'tutti' | 'sotto_scorta' | 'esauriti'.
  // Combinarli non serve a niente — un articolo a zero con una scorta minima e'
  // gia' in tutti e due — e due filtri accesi insieme si spiegano male.
  const [filtro, setFiltro] = useState('tutti');
  const [mostraArchiviati, setMostraArchiviati] = useState(false);
  const [inModifica, setInModifica] = useState(null);   // articolo, oppure 'nuovo'
  const [inMovimento, setInMovimento] = useState(null); // { articolo, tipo }
  const [inStorico, setInStorico] = useState(null);     // articolo
  const [daEliminare, setDaEliminare] = useState(null); // articolo

  const clienteId = profilo?.id;
  const abilitato = profilo?.tipo_cliente === 'azienda';

  const { data, isLoading } = useQuery({
    queryKey: ['inventario', clienteId, search, filtroCategoria, filtro, mostraArchiviati],
    enabled: Boolean(clienteId) && abilitato,
    queryFn: () => inventarioAPI.list(clienteId, {
      q: search || undefined,
      categoria: filtroCategoria || undefined,
      attivo: mostraArchiviati ? undefined : 'true',
      sotto_scorta: filtro === 'sotto_scorta' ? 'true' : undefined,
      esauriti: filtro === 'esauriti' ? 'true' : undefined,
    }),
  });
  const articoli = data?.articoli ?? [];

  const { data: categorie = [] } = useQuery({
    queryKey: ['inventario-categorie', clienteId],
    enabled: Boolean(clienteId) && abilitato,
    queryFn: () => inventarioAPI.categorie(clienteId),
  });

  const { data: riepilogo } = useQuery({
    queryKey: ['inventario-riepilogo', clienteId],
    enabled: Boolean(clienteId) && abilitato,
    queryFn: () => inventarioAPI.riepilogo(clienteId),
  });

  // Dopo ogni scrittura si ricaricano elenco, riepilogo e categorie: la categoria
  // puo' essere nuova, e il conteggio del sotto scorta cambia a ogni movimento.
  const aggiorna = () => {
    queryClient.invalidateQueries({ queryKey: ['inventario'] });
    queryClient.invalidateQueries({ queryKey: ['inventario-riepilogo'] });
    queryClient.invalidateQueries({ queryKey: ['inventario-categorie'] });
  };

  const archiviaMutation = useMutation({
    mutationFn: ({ id, attivo }) => inventarioAPI.archivia(id, attivo),
    onSuccess: aggiorna,
  });

  // Clic su un riquadro: accende quel filtro, oppure lo spegne se era gia' acceso.
  const cambiaFiltro = (quale) => setFiltro((f) => (f === quale ? 'tutti' : quale));

  if (profiloLoading) {
    return <ClienteLayout title="Inventario"><div style={styles.loading}>Caricamento...</div></ClienteLayout>;
  }

  // Un privato che arriva qui con un link diretto: la rotta esiste, il magazzino no.
  if (!abilitato) {
    return (
      <ClienteLayout title="Inventario">
        <div style={styles.empty}>
          <Boxes size={40} color="#9CA3AF" />
          <p>L'inventario e' disponibile solo per i clienti azienda.</p>
        </div>
      </ClienteLayout>
    );
  }

  const filtriAttivi = search || filtroCategoria || filtro !== 'tutti';

  return (
    <ClienteLayout title="Il mio inventario">
      {/* I tre riquadri sono anche i filtri: e' il gesto che viene naturale
          dopo aver letto "3 sotto scorta" — vedere quali. */}
      <div style={styles.statsRow}>
        <button
          type="button"
          onClick={() => setFiltro('tutti')}
          style={{ ...styles.statCard, ...(filtro === 'tutti' ? styles.statCardAttiva : {}) }}
        >
          <div style={styles.statLabel}>Articoli</div>
          <div style={styles.statValue}>{riepilogo?.articoli ?? '—'}</div>
          <div style={styles.statHint}>{filtro === 'tutti' ? 'stai vedendo tutti' : 'vedi tutti'}</div>
        </button>

        <button
          type="button"
          onClick={() => cambiaFiltro('sotto_scorta')}
          style={{ ...styles.statCard, ...(filtro === 'sotto_scorta' ? styles.statCardAttivaRossa : {}) }}
        >
          <div style={styles.statLabel}>Sotto scorta</div>
          <div style={{ ...styles.statValue, color: riepilogo?.sotto_scorta ? '#DC2626' : '#111827' }}>
            {riepilogo?.sotto_scorta ?? '—'}
          </div>
          <div style={styles.statHint}>{filtro === 'sotto_scorta' ? 'tocca per togliere' : 'tocca per vedere quali'}</div>
        </button>

        <button
          type="button"
          onClick={() => cambiaFiltro('esauriti')}
          style={{ ...styles.statCard, ...(filtro === 'esauriti' ? styles.statCardAttivaArancio : {}) }}
        >
          <div style={styles.statLabel}>Esauriti</div>
          <div style={{ ...styles.statValue, color: riepilogo?.esauriti ? '#D97706' : '#111827' }}>
            {riepilogo?.esauriti ?? '—'}
          </div>
          <div style={styles.statHint}>{filtro === 'esauriti' ? 'tocca per togliere' : 'tocca per vedere quali'}</div>
        </button>
      </div>

      <div style={{ ...styles.toolbar, ...(isCompact ? styles.toolbarCompact : {}) }}>
        <div style={styles.searchBox}>
          <Search size={16} color="#9CA3AF" />
          <input
            style={styles.searchInput}
            placeholder="Cerca per nome, codice, ubicazione..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button style={styles.clearBtn} onClick={() => setSearch('')}><X size={14} /></button>}
        </div>
        <select style={styles.select} value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}>
          <option value="">Tutte le categorie</option>
          {categorie.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          style={{ ...styles.chip, ...(mostraArchiviati ? styles.chipAttivo : {}) }}
          onClick={() => setMostraArchiviati((v) => !v)}
        >
          <Archive size={14} /> Archiviati
        </button>
        <button style={styles.primaryBtn} onClick={() => setInModifica('nuovo')}>
          <Plus size={16} /> Nuovo articolo
        </button>
      </div>

      {isLoading ? (
        <div style={styles.loading}>Caricamento...</div>
      ) : articoli.length === 0 ? (
        <div style={styles.empty}>
          <Boxes size={40} color="#9CA3AF" />
          <p>{filtriAttivi ? 'Nessun articolo con questi filtri.' : 'Il tuo inventario e\' vuoto.'}</p>
          {filtriAttivi ? (
            <button style={styles.secondaryBtn} onClick={() => { setSearch(''); setFiltroCategoria(''); setFiltro('tutti'); }}>
              Togli i filtri
            </button>
          ) : (
            <p style={styles.emptyHint}>
              Aggiungi il materiale che tieni in magazzino: quantita, scorta minima e dove si trova.
              Ti segnaliamo gli articoli che scendono sotto la soglia.
            </p>
          )}
        </div>
      ) : (
        <div style={styles.list}>
          {articoli.map((a) => {
            const sottoScorta = parseFloat(a.quantita_minima) > 0 && parseFloat(a.quantita) <= parseFloat(a.quantita_minima);
            return (
              <div key={a.id} style={{ ...styles.card, ...(a.attivo ? {} : styles.cardArchiviata) }}>
                <div style={styles.cardTop}>
                  <div style={{ ...styles.iconBox, backgroundColor: (sottoScorta ? '#DC2626' : ACCENT) + '15' }}>
                    <Boxes size={22} color={sottoScorta ? '#DC2626' : ACCENT} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.nome}>{a.nome}</div>
                    <div style={styles.meta}>
                      {a.codice && <span style={styles.codice}>{a.codice}</span>}
                      {a.categoria && <span>{a.categoria}</span>}
                      {!a.attivo && <span style={styles.archiviatoTag}>archiviato</span>}
                    </div>
                    {a.ubicazione && <div style={styles.ubicazione}><MapPin size={12} /> {a.ubicazione}</div>}
                  </div>
                  <div style={styles.qtaBox}>
                    <div style={{ ...styles.qtaValore, color: sottoScorta ? '#DC2626' : '#111827' }}>{qta(a.quantita)}</div>
                    <div style={styles.qtaUnita}>{a.unita_misura}</div>
                  </div>
                </div>

                {a.descrizione && <div style={styles.descrizione}>{a.descrizione}</div>}

                {sottoScorta && (
                  <div style={styles.alertBadge}>
                    <AlertTriangle size={14} /> Sotto la scorta minima ({qta(a.quantita_minima)} {a.unita_misura})
                  </div>
                )}

                <div style={styles.azioni}>
                  {a.attivo && (
                    <>
                      <button style={styles.azioneBtn} onClick={() => setInMovimento({ articolo: a, tipo: 'carico' })}>
                        <ArrowDownCircle size={15} color="#059669" /> Carico
                      </button>
                      <button style={styles.azioneBtn} onClick={() => setInMovimento({ articolo: a, tipo: 'scarico' })}>
                        <ArrowUpCircle size={15} color="#DC2626" /> Scarico
                      </button>
                      <button style={styles.azioneBtn} onClick={() => setInMovimento({ articolo: a, tipo: 'rettifica' })}>
                        <ClipboardCheck size={15} color="#6B7280" /> Conteggio
                      </button>
                    </>
                  )}
                  <button style={styles.azioneBtn} onClick={() => setInStorico(a)}>
                    <History size={15} color="#6B7280" /> Movimenti
                  </button>
                  {a.attivo && (
                    <button style={styles.azioneBtn} onClick={() => setInModifica(a)}>
                      <Edit2 size={15} color="#6B7280" /> Modifica
                    </button>
                  )}
                  <button
                    style={styles.azioneBtn}
                    onClick={() => archiviaMutation.mutate({ id: a.id, attivo: !a.attivo })}
                  >
                    {a.attivo
                      ? <><Archive size={15} color="#6B7280" /> Archivia</>
                      : <><RotateCcw size={15} color="#6B7280" /> Ripristina</>}
                  </button>
                  <button style={styles.azioneBtnRossa} onClick={() => setDaEliminare(a)}>
                    <Trash2 size={15} /> Elimina
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {inModifica && (
        <ArticoloFormModal
          clienteId={clienteId}
          articolo={inModifica === 'nuovo' ? null : inModifica}
          categorie={categorie}
          onClose={() => setInModifica(null)}
          onSaved={() => { setInModifica(null); aggiorna(); }}
        />
      )}

      {inMovimento && (
        <MovimentoModal
          articolo={inMovimento.articolo}
          tipoIniziale={inMovimento.tipo}
          onClose={() => setInMovimento(null)}
          onSaved={() => { setInMovimento(null); aggiorna(); }}
        />
      )}

      {inStorico && (
        <StoricoModal articolo={inStorico} onClose={() => setInStorico(null)} />
      )}

      {daEliminare && (
        <EliminaModal
          articolo={daEliminare}
          onClose={() => setDaEliminare(null)}
          onDeleted={() => { setDaEliminare(null); aggiorna(); }}
        />
      )}
    </ClienteLayout>
  );
}

// ============================================================
// Anagrafica dell'articolo. La quantita' si inserisce SOLO alla creazione:
// dopo, cambia con un movimento, altrimenti il registro mentirebbe.
// ============================================================
function ArticoloFormModal({ clienteId, articolo, categorie, onClose, onSaved }) {
  const nuovo = !articolo;
  const [form, setForm] = useState({
    codice: articolo?.codice || '',
    nome: articolo?.nome || '',
    descrizione: articolo?.descrizione || '',
    categoria: articolo?.categoria || '',
    unita_misura: articolo?.unita_misura || 'pz',
    quantita: '',
    quantita_minima: articolo ? qta(articolo.quantita_minima) : '',
    ubicazione: articolo?.ubicazione || '',
    note: articolo?.note || '',
  });
  const [errore, setErrore] = useState('');

  // Calcolati a ogni render: l'avviso compare mentre si scrive, sotto al campo.
  const errQuantita = nuovo ? erroreNumero(form.quantita) : null;
  const errMinima = erroreNumero(form.quantita_minima);

  const salva = useMutation({
    mutationFn: (payload) => (nuovo
      ? inventarioAPI.crea(clienteId, payload)
      : inventarioAPI.modifica(articolo.id, payload)),
    onSuccess: onSaved,
    onError: (e) => setErrore(e.response?.data?.error || 'Errore durante il salvataggio'),
  });

  const onSubmit = (e) => {
    e.preventDefault();
    setErrore('');
    if (!form.nome.trim()) return setErrore('Il nome e\' obbligatorio');
    if (errQuantita || errMinima) return setErrore('Correggi i campi segnati in rosso');
    const payload = { ...form };
    if (!nuovo) delete payload.quantita; // ignorata dal backend, non mandarla nemmeno
    salva.mutate(payload);
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <form style={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{nuovo ? 'Nuovo articolo' : 'Modifica articolo'}</h2>
          <button type="button" style={styles.iconBtn} onClick={onClose}><X size={18} /></button>
        </div>

        <div style={styles.modalBody}>
          <label style={styles.label}>Nome *</label>
          <input
            style={styles.input}
            value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
            placeholder="Es. Filtro aria 20x20"
            autoFocus
          />

          <div style={styles.row}>
            <div style={styles.col}>
              <label style={styles.label}>Codice</label>
              <input
                style={styles.input}
                value={form.codice}
                onChange={(e) => setForm({ ...form, codice: e.target.value })}
                placeholder="facoltativo"
              />
            </div>
            <div style={styles.col}>
              <label style={styles.label}>Unita di misura</label>
              <input
                style={styles.input}
                value={form.unita_misura}
                onChange={(e) => setForm({ ...form, unita_misura: e.target.value })}
                placeholder="pz, m, kg, l"
              />
            </div>
          </div>

          <label style={styles.label}>Categoria</label>
          <input
            style={styles.input}
            list="categorie-inventario"
            value={form.categoria}
            onChange={(e) => setForm({ ...form, categoria: e.target.value })}
            placeholder="facoltativa"
          />
          <datalist id="categorie-inventario">
            {categorie.map((c) => <option key={c} value={c} />)}
          </datalist>

          <div style={styles.row}>
            {nuovo && (
              <div style={styles.col}>
                <label style={styles.label}>Quantita iniziale</label>
                <input
                  style={{ ...styles.input, ...(errQuantita ? styles.inputErrato : {}) }}
                  inputMode="decimal"
                  value={form.quantita}
                  onChange={(e) => setForm({ ...form, quantita: e.target.value })}
                  placeholder="0"
                />
                {errQuantita
                  ? <div style={styles.erroreCampo}><AlertTriangle size={13} /> {errQuantita}</div>
                  : <div style={styles.hint}>Se la lasci vuota parte da zero.</div>}
              </div>
            )}
            <div style={styles.col}>
              <label style={styles.label}>Scorta minima</label>
              <input
                style={{ ...styles.input, ...(errMinima ? styles.inputErrato : {}) }}
                inputMode="decimal"
                value={form.quantita_minima}
                onChange={(e) => setForm({ ...form, quantita_minima: e.target.value })}
                placeholder="0"
              />
              {errMinima
                ? <div style={styles.erroreCampo}><AlertTriangle size={13} /> {errMinima}</div>
                : <div style={styles.hint}>Sotto questa soglia l'articolo viene segnalato. Lascia 0 per non essere avvisato.</div>}
            </div>
          </div>

          <label style={styles.label}>Ubicazione</label>
          <input
            style={styles.input}
            value={form.ubicazione}
            onChange={(e) => setForm({ ...form, ubicazione: e.target.value })}
            placeholder="Es. Scaffale B, ripiano 2"
          />

          <label style={styles.label}>Note</label>
          <textarea
            style={{ ...styles.input, minHeight: '70px', resize: 'vertical' }}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />

          {!nuovo && (
            <div style={styles.hint}>
              La giacenza ({qta(articolo.quantita)} {articolo.unita_misura}) non si modifica da qui:
              registra un carico, uno scarico o un conteggio.
            </div>
          )}

          {errore && <div style={styles.errore}>{errore}</div>}
        </div>

        <div style={styles.modalActions}>
          <button type="button" style={styles.secondaryBtn} onClick={onClose}>Annulla</button>
          <button
            type="submit"
            style={{ ...styles.primaryBtn, ...(errQuantita || errMinima ? styles.btnDisabilitato : {}) }}
            disabled={salva.isPending || Boolean(errQuantita || errMinima)}
          >
            {salva.isPending ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// Il movimento: l'unico modo di cambiare una giacenza.
// ============================================================
function MovimentoModal({ articolo, tipoIniziale, onClose, onSaved }) {
  const [tipo, setTipo] = useState(tipoIniziale);
  const [quantita, setQuantita] = useState('');
  const [nota, setNota] = useState('');
  const [errore, setErrore] = useState('');

  const registra = useMutation({
    mutationFn: (payload) => inventarioAPI.movimento(articolo.id, payload),
    onSuccess: onSaved,
    onError: (e) => setErrore(e.response?.data?.error || 'Errore durante il salvataggio'),
  });

  const errQuantita = erroreNumero(quantita);
  const attuale = parseFloat(articolo.quantita);
  const inserita = parseFloat(String(quantita).replace(',', '.'));
  // Anteprima del risultato: chi scarica vuole vedere subito dove va a finire.
  // Si calcola solo se il numero e' pulito, altrimenti direbbe bugie.
  const risultante = !errQuantita && String(quantita).trim() !== '' && Number.isFinite(inserita)
    ? (tipo === 'carico' ? attuale + inserita : tipo === 'scarico' ? attuale - inserita : inserita)
    : null;

  const onSubmit = (e) => {
    e.preventDefault();
    setErrore('');
    if (errQuantita) return setErrore(errQuantita);
    if (String(quantita).trim() === '') return setErrore('Scrivi una quantita');
    if (tipo !== 'rettifica' && inserita === 0) return setErrore('Scrivi una quantita maggiore di zero');
    registra.mutate({ tipo, quantita, nota });
  };

  const TIPI = [
    { id: 'carico', label: 'Carico', desc: 'Ho aggiunto materiale' },
    { id: 'scarico', label: 'Scarico', desc: 'Ho usato materiale' },
    { id: 'rettifica', label: 'Conteggio', desc: 'Ho contato allo scaffale' },
  ];

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <form style={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{articolo.nome}</h2>
          <button type="button" style={styles.iconBtn} onClick={onClose}><X size={18} /></button>
        </div>

        <div style={styles.modalBody}>
          <div style={styles.giacenzaAttuale}>
            In giacenza ora: <strong>{qta(articolo.quantita)} {articolo.unita_misura}</strong>
          </div>

          <div style={styles.tipoRow}>
            {TIPI.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => { setTipo(t.id); setErrore(''); }}
                style={{ ...styles.tipoBtn, ...(tipo === t.id ? styles.tipoBtnAttivo : {}) }}
              >
                <span style={styles.tipoLabel}>{t.label}</span>
                <span style={styles.tipoDesc}>{t.desc}</span>
              </button>
            ))}
          </div>

          <label style={styles.label}>
            {tipo === 'rettifica' ? `Quanti ce ne sono davvero (${articolo.unita_misura})` : `Quantita (${articolo.unita_misura})`}
          </label>
          <input
            style={{ ...styles.input, fontSize: '1.2rem', fontWeight: 600, ...(errQuantita ? styles.inputErrato : {}) }}
            inputMode="decimal"
            value={quantita}
            onChange={(e) => { setQuantita(e.target.value); setErrore(''); }}
            placeholder="0"
            autoFocus
          />

          {errQuantita ? (
            <div style={styles.erroreCampo}><AlertTriangle size={13} /> {errQuantita}</div>
          ) : risultante !== null ? (
            <div style={{ ...styles.hint, color: risultante < 0 ? '#DC2626' : '#6B7280' }}>
              {risultante < 0
                ? `Non ci sono abbastanza pezzi: in giacenza ce ne sono ${qta(articolo.quantita)}.`
                : `Dopo il movimento: ${qta(risultante)} ${articolo.unita_misura}`}
            </div>
          ) : null}

          <label style={styles.label}>Nota</label>
          <input
            style={styles.input}
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder={tipo === 'scarico' ? 'Es. usati per la sala macchine' : 'facoltativa'}
            maxLength={255}
          />

          {errore && <div style={styles.errore}>{errore}</div>}
        </div>

        <div style={styles.modalActions}>
          <button type="button" style={styles.secondaryBtn} onClick={onClose}>Annulla</button>
          <button
            type="submit"
            style={{ ...styles.primaryBtn, ...(errQuantita ? styles.btnDisabilitato : {}) }}
            disabled={registra.isPending || Boolean(errQuantita)}
          >
            {registra.isPending ? 'Registrazione...' : 'Registra'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// Cancellazione definitiva.
//
// Non usa window.confirm: una finestra del browser qui direbbe una riga sola e
// non potrebbe dire la cosa che conta, cioe' che se ne va anche il registro.
// ============================================================
function EliminaModal({ articolo, onClose, onDeleted }) {
  const [errore, setErrore] = useState('');
  const elimina = useMutation({
    mutationFn: () => inventarioAPI.elimina(articolo.id),
    onSuccess: onDeleted,
    onError: (e) => setErrore(e.response?.data?.error || 'Errore durante la cancellazione'),
  });

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Eliminare "{articolo.nome}"?</h2>
          <button type="button" style={styles.iconBtn} onClick={onClose}><X size={18} /></button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.avvisoRosso}>
            <Trash2 size={16} />
            <div>
              L'articolo sparisce dall'inventario <strong>insieme a tutto il suo registro
              dei movimenti</strong>. Non si torna indietro.
            </div>
          </div>
          <div style={styles.hint}>
            Se ti serve solo toglierlo dall'elenco ma vuoi conservarne la storia,
            usa <strong>Archivia</strong>: lo ritrovi con il filtro "Archiviati".
          </div>
          {errore && <div style={styles.errore}>{errore}</div>}
        </div>
        <div style={styles.modalActions}>
          <button type="button" style={styles.secondaryBtn} onClick={onClose}>Annulla</button>
          <button
            type="button"
            style={styles.dangerBtn}
            onClick={() => elimina.mutate()}
            disabled={elimina.isPending}
          >
            {elimina.isPending ? 'Eliminazione...' : 'Elimina definitivamente'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Lo storico: chi ha toccato cosa, quando e di quanto.
// ============================================================
function StoricoModal({ articolo, onClose }) {
  const { data: movimenti = [], isLoading } = useQuery({
    queryKey: ['inventario-movimenti', articolo.id],
    queryFn: () => inventarioAPI.movimenti(articolo.id),
  });

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Movimenti — {articolo.nome}</h2>
          <button type="button" style={styles.iconBtn} onClick={onClose}><X size={18} /></button>
        </div>
        <div style={styles.modalBody}>
          {isLoading ? (
            <div style={styles.loading}>Caricamento...</div>
          ) : movimenti.length === 0 ? (
            <div style={styles.hint}>Nessun movimento registrato.</div>
          ) : (
            <div style={styles.storicoList}>
              {movimenti.map((m) => {
                const delta = parseFloat(m.delta);
                return (
                  <div key={m.id} style={styles.storicoRiga}>
                    <div style={styles.storicoTop}>
                      <span style={styles.storicoTipo}>{TIPO_LABEL[m.tipo] || m.tipo}</span>
                      <span style={{ ...styles.storicoDelta, color: delta >= 0 ? '#059669' : '#DC2626' }}>
                        {delta > 0 ? '+' : ''}{qta(delta)} {articolo.unita_misura}
                      </span>
                    </div>
                    <div style={styles.storicoDettaglio}>
                      da {qta(m.quantita_precedente)} a {qta(m.quantita_risultante)} {articolo.unita_misura}
                    </div>
                    {m.nota && <div style={styles.storicoNota}>{m.nota}</div>}
                    <div style={styles.storicoFirma}>
                      {dataOra(m.created_at)} — {m.utente_nome || 'utente rimosso'}
                      {m.utente_ruolo === 'admin' && ' (assistenza)'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div style={styles.modalActions}>
          <button type="button" style={styles.secondaryBtn} onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  empty: { padding: '3rem 1.5rem', textAlign: 'center', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem', backgroundColor: 'white', borderRadius: '12px' },
  emptyHint: { fontSize: '0.85rem', color: '#9CA3AF', maxWidth: '420px' },

  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' },
  statCard: { backgroundColor: 'white', borderRadius: '12px', padding: '0.9rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '2px solid transparent', textAlign: 'left', cursor: 'pointer', font: 'inherit', minHeight: '44px', display: 'block', width: '100%' },
  statCardAttiva: { borderColor: ACCENT, backgroundColor: ACCENT + '0D' },
  statCardAttivaRossa: { borderColor: '#DC2626', backgroundColor: '#FEF2F2' },
  statCardAttivaArancio: { borderColor: '#D97706', backgroundColor: '#FFFBEB' },
  statLabel: { fontSize: '0.75rem', color: '#6B7280' },
  statValue: { fontSize: '1.5rem', fontWeight: 700, marginTop: '0.15rem' },
  statHint: { fontSize: '0.68rem', color: '#9CA3AF', marginTop: '0.1rem' },

  toolbar: { display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' },
  toolbarCompact: { gap: '0.5rem' },
  searchBox: { display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '0.5rem 0.7rem', flex: '1 1 220px', minWidth: 0 },
  searchInput: { border: 'none', outline: 'none', fontSize: '0.9rem', flex: 1, minWidth: 0 },
  clearBtn: { border: 'none', background: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex', padding: 0 },
  select: { padding: '0.55rem 0.7rem', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '0.9rem', backgroundColor: 'white', minHeight: '40px' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 0.75rem', border: '1px solid #E5E7EB', borderRadius: '10px', backgroundColor: 'white', color: '#374151', fontSize: '0.85rem', cursor: 'pointer', minHeight: '40px' },
  chipAttivo: { backgroundColor: ACCENT + '15', borderColor: ACCENT, color: ACCENT, fontWeight: 600 },
  primaryBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  secondaryBtn: { padding: '0.55rem 1rem', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  dangerBtn: { padding: '0.55rem 1rem', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  btnDisabilitato: { opacity: 0.45, cursor: 'not-allowed' },

  list: { display: 'flex', flexDirection: 'column', gap: '0.85rem' },
  card: { backgroundColor: 'white', borderRadius: '12px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  cardArchiviata: { opacity: 0.6 },
  cardTop: { display: 'flex', alignItems: 'center', gap: '0.8rem' },
  iconBox: { width: '44px', height: '44px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  nome: { fontWeight: 700, fontSize: '1rem' },
  meta: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.8rem', color: '#6B7280', marginTop: '0.1rem' },
  codice: { fontFamily: 'monospace', backgroundColor: '#F3F4F6', padding: '0 0.35rem', borderRadius: '4px' },
  archiviatoTag: { color: '#9CA3AF', fontStyle: 'italic' },
  ubicazione: { display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem', color: '#9CA3AF', marginTop: '0.15rem' },
  qtaBox: { textAlign: 'right', flexShrink: 0 },
  qtaValore: { fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.1 },
  qtaUnita: { fontSize: '0.72rem', color: '#9CA3AF' },
  descrizione: { fontSize: '0.85rem', color: '#4B5563', marginTop: '0.6rem' },
  alertBadge: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.35rem 0.7rem', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 600, marginTop: '0.7rem' },
  azioni: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.85rem', paddingTop: '0.75rem', borderTop: '1px solid #F3F4F6' },
  azioneBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.45rem 0.7rem', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '0.82rem', color: '#374151', cursor: 'pointer', minHeight: '38px' },
  azioneBtnRossa: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.45rem 0.7rem', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', fontSize: '0.82rem', color: '#B91C1C', cursor: 'pointer', minHeight: '38px' },

  modalOverlay: { position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' },
  modal: { backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflow: 'auto', display: 'flex', flexDirection: 'column' },
  modalHeader: { padding: '1.1rem 1.25rem', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' },
  modalTitle: { margin: 0, fontSize: '1.05rem', fontWeight: 700 },
  modalBody: { padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  modalActions: { padding: '1rem 1.25rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '0.6rem' },
  iconBtn: { border: 'none', background: 'none', cursor: 'pointer', color: '#6B7280', display: 'flex', padding: '0.25rem' },

  label: { fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginTop: '0.6rem' },
  input: { width: '100%', padding: '0.6rem 0.7rem', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  inputErrato: { borderColor: '#DC2626', backgroundColor: '#FEF2F2' },
  row: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  col: { flex: '1 1 140px', minWidth: 0 },
  hint: { fontSize: '0.78rem', color: '#6B7280', marginTop: '0.35rem' },
  erroreCampo: { display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.78rem', color: '#DC2626', fontWeight: 600, marginTop: '0.35rem' },
  errore: { marginTop: '0.75rem', padding: '0.6rem 0.75rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '0.85rem' },
  avvisoRosso: { display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.75rem 0.85rem', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', borderRadius: '10px', fontSize: '0.88rem', lineHeight: 1.45 },

  giacenzaAttuale: { padding: '0.6rem 0.75rem', backgroundColor: '#F9FAFB', borderRadius: '8px', fontSize: '0.9rem', color: '#374151' },
  tipoRow: { display: 'flex', gap: '0.5rem', marginTop: '0.85rem', flexWrap: 'wrap' },
  tipoBtn: { flex: '1 1 100px', display: 'flex', flexDirection: 'column', gap: '0.15rem', padding: '0.6rem', border: '1px solid #E5E7EB', borderRadius: '10px', backgroundColor: 'white', cursor: 'pointer', textAlign: 'left', minHeight: '44px' },
  tipoBtnAttivo: { borderColor: ACCENT, backgroundColor: ACCENT + '10' },
  tipoLabel: { fontSize: '0.88rem', fontWeight: 700, color: '#111827' },
  tipoDesc: { fontSize: '0.72rem', color: '#6B7280' },

  storicoList: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  storicoRiga: { padding: '0.7rem 0.8rem', backgroundColor: '#F9FAFB', borderRadius: '10px' },
  storicoTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' },
  storicoTipo: { fontSize: '0.85rem', fontWeight: 700, color: '#111827' },
  storicoDelta: { fontSize: '0.9rem', fontWeight: 700 },
  storicoDettaglio: { fontSize: '0.78rem', color: '#6B7280', marginTop: '0.15rem' },
  storicoNota: { fontSize: '0.82rem', color: '#374151', marginTop: '0.3rem', fontStyle: 'italic' },
  storicoFirma: { fontSize: '0.72rem', color: '#9CA3AF', marginTop: '0.3rem' },
};
